import { useEffect, useMemo, useRef, useState } from "react";
import {
  CORREOS,
  correoCarTruck,
  correoCustodia,
  correoPosiciones,
  direcciones,
  filasCarTruckBUE,
  filasCarTruckRGD,
  fuenteCarTruckBUE,
  paraMandar,
} from "../../shared/correosManana";
import { mailto } from "../../shared/ordenGasoil";
import { traerDestinos, guardarDestino, choferesPadron } from "../../services/correosManana";
import { traerSalidas } from "../../services/salidasBitren";
import { traerPlanilla } from "../../services/espacio";
import { padron } from "../../services/torrecillas";

/* Los correos de la mañana de Satelital, uno por botón (ticket #456; Lautaro
 * Díaz, 19/10/2026: «ver forma de generar el correo con un botón […] que sea
 * entendible no sólo para mí sino para mis compañeros»). Se elige el correo,
 * se ve tal cual va, y «Abrir en mi correo» / «Copiar el correo» lo pasan a
 * su programa de correo, como en Enganches y en la Orden de gas oil. De acá
 * no sale ningún mail.
 *
 * Mientras un correo esté a prueba, se arma sólo para el que lo genera
 * (`paraMandar`). El Para, la copia y el paso a real los cambia él acá mismo.
 *
 * Las Torrecillas cargadas arriba alimentan la custodia y el Car-Truck de Río
 * Grande; las salidas de Buenos Aires del día y sus planillas (ONLY BITRENES y
 * ARRIBOS Y CORTES) se leen al elegir el correo que las usa.
 *
 * El Car-Truck de Buenos Aires sale del Excel de Gula si él lo soltó en
 * «Salidas de Buenos Aires» y es del mismo día (Lautaro, 27/10, ticket 536):
 * `gula` es lo que FlotaBot ya leyó de ese Excel, sin volver a mandarlo. Si
 * no, de la Previsión, como antes (`fuenteCarTruckBUE`).
 */

async function copiarTexto(t) {
  try {
    await navigator.clipboard.writeText(t);
    return true;
  } catch {
    return false;
  }
}

async function copiarCorreo(c, nodo) {
  try {
    if (navigator.clipboard?.write && typeof window.ClipboardItem !== "undefined") {
      await navigator.clipboard.write([
        new window.ClipboardItem({
          "text/html": new Blob([c.html], { type: "text/html" }),
          "text/plain": new Blob([c.texto], { type: "text/plain" }),
        }),
      ]);
      return true;
    }
  } catch {
    /* cae a la selección */
  }
  try {
    if (!nodo || !document.execCommand) return copiarTexto(c.texto);
    const sel = window.getSelection();
    const rango = document.createRange();
    rango.selectNodeContents(nodo);
    sel.removeAllRanges();
    sel.addRange(rango);
    const ok = document.execCommand("copy");
    sel.removeAllRanges();
    return ok || copiarTexto(c.texto);
  } catch {
    return copiarTexto(c.texto);
  }
}

const lista = (v) => (v || []).join(", ");

export default function CorreosDeLaManana({
  bloques = [], fecha, datos = null, planillaIds = [], yo = "", hora, gula = null,
}) {
  const [destinos, setDestinos] = useState(null);
  const [error, setError] = useState("");
  const [clave, setClave] = useState("");
  const [salidasBUE, setSalidasBUE] = useState(null);
  const [planillas, setPlanillas] = useState(null);
  const [padronPropio, setPadronPropio] = useState(null);
  const [choferes, setChoferes] = useState(null);
  const [ocupado, setOcupado] = useState("");
  const [editando, setEditando] = useState(null); // { para, cc }
  const [aviso, setAviso] = useState("");
  const vista = useRef(null);

  useEffect(() => {
    let vivo = true;
    traerDestinos()
      .then((d) => vivo && setDestinos(d))
      .catch(() => vivo && setError("No se pudo consultar."));
    return () => {
      vivo = false;
    };
  }, []);

  const elegido = CORREOS.find((c) => c.clave === clave) || null;
  const dia = fecha || new Date().toISOString().slice(0, 10);
  const padronUsado = datos || padronPropio;
  const fuenteBUE = fuenteCarTruckBUE(gula, dia);

  // Lo que cada correo necesita leer, recién cuando se lo elige.
  useEffect(() => {
    if (!elegido) return undefined;
    let vivo = true;
    setAviso("");
    (async () => {
      try {
        if (elegido.tipo === "cartruck") {
          setOcupado("leyendo");
          // Con el Excel de Gula del día no hace falta la Previsión.
          const bue = elegido.sentido === "BUE";
          const [s, p, ch] = await Promise.all([
            !bue || fuenteBUE.delExcel ? null : traerSalidas(dia),
            padronUsado ? null : padron(),
            !bue || choferes ? null : choferesPadron(),
          ]);
          if (!vivo) return;
          if (bue && !fuenteBUE.delExcel) setSalidasBUE(s || { bitrenes: [], comunes: [] });
          if (p) setPadronPropio(p);
          if (ch) setChoferes(ch);
        }
        if (elegido.tipo === "posiciones" && planillas === null) {
          setOcupado("leyendo");
          const ids = [...new Set(planillaIds.filter(Boolean))];
          const leidas = await Promise.all(ids.map((id) => traerPlanilla(id).catch(() => null)));
          if (vivo) setPlanillas(leidas.filter(Boolean));
        }
      } catch {
        if (vivo) setError("No se pudo consultar.");
      } finally {
        if (vivo) setOcupado("");
      }
    })();
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave, dia, fuenteBUE.delExcel]);

  const correo = useMemo(() => {
    if (!elegido) return null;
    if (elegido.tipo === "custodia") return correoCustodia({ aseguradora: elegido.aseguradora, cliente: elegido.cliente, bloques, fecha: dia, hora });
    if (elegido.tipo === "cartruck") {
      const salidas = fuenteBUE.delExcel ? gula : salidasBUE;
      const filas =
        elegido.sentido === "RGD"
          ? filasCarTruckRGD(bloques, padronUsado || {}, dia)
          : salidas
            ? filasCarTruckBUE(salidas, { ...(padronUsado || {}), choferes }, dia)
            : [];
      return correoCarTruck({ sentido: elegido.sentido, filas, fecha: dia });
    }
    return correoPosiciones({ cliente: elegido.cliente, planillas: planillas || [], fecha: dia });
  }, [elegido, bloques, dia, hora, salidasBUE, padronUsado, planillas, gula, fuenteBUE.delExcel, choferes]);

  /* «Limpiar» (Lautaro, 28/10, ticket 546: «en todos ponele un limpiar, para
     no tener que refrescar la página»): suelta el correo elegido y lo que se
     leyó para armarlo —sus planillas, las salidas de Buenos Aires, los
     padrones—, así el próximo que elija se arma con lo de ahora. */
  const limpiar = () => {
    setClave("");
    setEditando(null);
    setAviso("");
    setSalidasBUE(null);
    setPlanillas(null);
    setPadronPropio(null);
    setChoferes(null);
  };

  const dest = (destinos && clave && destinos[clave]) || { para: [], cc: [], real: false };
  const aMandar = correo ? paraMandar(correo, dest, yo) : null;

  const guardar = async (cambio) => {
    setOcupado("guardando");
    setAviso("");
    try {
      const nuevo = await guardarDestino(clave, { para: dest.para, cc: dest.cc, real: dest.real, ...cambio });
      setDestinos((d) => ({ ...(d || {}), [clave]: nuevo }));
      setEditando(null);
      setAviso("Guardado.");
    } catch {
      setAviso("No se pudo guardar.");
    } finally {
      setOcupado("");
    }
  };

  const copiar = async () => {
    const ok = await copiarCorreo(aMandar, vista.current);
    setAviso(ok ? "El correo: copiado." : "No se pudo copiar.");
  };

  if (error && !destinos) {
    return (
      <div style={{ marginTop: 24 }}>
        <h3>Correos de la mañana</h3>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 24 }}>
      <h3>Correos de la mañana</h3>
      <div className="tk-acciones" style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {CORREOS.map((c) => (
          <button
            key={c.clave}
            type="button"
            className={`tk-boton${c.clave === clave ? " tk-boton--fuerte" : ""}`}
            onClick={() => {
              setClave(c.clave);
              setEditando(null);
            }}
          >
            {c.nombre}
            {destinos?.[c.clave]?.real ? "" : " · prueba"}
          </button>
        ))}
      </div>

      {elegido && correo && (
        <div className="panel-aviso suave" style={{ marginTop: 12 }}>
          {ocupado === "leyendo" ? (
            <p className="tk-meta">Consultando…</p>
          ) : (
            <>
              <p className="tk-meta">
                <b>Para:</b> {lista(aMandar.para) || "—"}
                <br />
                <b>Copia:</b> {lista(aMandar.copia) || "—"}
                <br />
                <b>Asunto:</b> {aMandar.asunto}
              </p>
              {elegido.tipo === "cartruck" && elegido.sentido === "BUE" && (
                <p className="tk-meta">{fuenteBUE.nota}</p>
              )}
              {correo.filas === 0 && <p className="tk-meta">Sin unidades para este correo.</p>}
              <div className="tk-acciones" style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                <a className="tk-boton tk-boton--fuerte" href={mailto(aMandar)}>
                  Abrir en mi correo
                </a>
                <button type="button" className="tk-boton" onClick={copiar}>
                  Copiar el correo
                </button>
                <button
                  type="button"
                  className="tk-boton"
                  onClick={() => setEditando({ para: lista(dest.para), cc: lista(dest.cc) })}
                >
                  Destinatarios
                </button>
                <button
                  type="button"
                  className="tk-boton"
                  disabled={!!ocupado || (!dest.real && direcciones(dest.para).length === 0)}
                  onClick={() => guardar({ real: !dest.real })}
                >
                  {dest.real ? "Volver a prueba" : "Pasar a real"}
                </button>
                <button type="button" className="tk-boton" onClick={limpiar}>
                  Limpiar
                </button>
              </div>
              {editando && (
                <div style={{ marginTop: 8, display: "grid", gap: 6, maxWidth: 640 }}>
                  <label>
                    Para
                    <textarea
                      rows={2}
                      style={{ width: "100%" }}
                      value={editando.para}
                      onChange={(e) => setEditando({ ...editando, para: e.target.value })}
                    />
                  </label>
                  <label>
                    Copia
                    <textarea
                      rows={2}
                      style={{ width: "100%" }}
                      value={editando.cc}
                      onChange={(e) => setEditando({ ...editando, cc: e.target.value })}
                    />
                  </label>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button
                      type="button"
                      className="tk-boton tk-boton--fuerte"
                      disabled={!!ocupado}
                      onClick={() => guardar({ para: direcciones(editando.para), cc: direcciones(editando.cc) })}
                    >
                      Guardar
                    </button>
                    <button type="button" className="tk-boton" onClick={() => setEditando(null)}>
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
              {aviso && <p className="tk-meta">{aviso}</p>}
              <div
                ref={vista}
                data-testid="vista-correo-manana"
                style={{ background: "#fff", color: "#000", padding: 12, marginTop: 8, overflowX: "auto" }}
                dangerouslySetInnerHTML={{ __html: aMandar.html }}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}
